/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../../../../core/services/';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { Observable } from 'rxjs';
import { PulseHAGlobalModel } from '../models/pulseHAGlobal.model';
import { PulseHAModel } from '../models/pulseHAModel';
import { SSLEnum } from '../enums/api';

/**
 * Network Service
 * Note: This class is to be used to make API calls
 *       and return with a structured response.
 */
@Injectable()
export class PulseHAApiService {
    constructor(
        private api: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * Get the pulseha conf via the api
     * @returns {Observable}
     */
    public getConfig(options?: any): Observable<PulseHAModel> {
        return new Observable(
            (observer) => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.api.get(
                    SSLEnum.GET_PULSEHA_CONF,
                    moduleAuth
                ).subscribe(
                    (response) => {
                        observer.next(response);
                        observer.complete();
                    },
                    error => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Update pulseha conf via the api
     * @returns {Observable<PulseHAModel>}
     */
    public updateConfig(config: PulseHAModel, options?: any) {
        return new Observable(
            (observer) => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.api.put(
                    SSLEnum.PUT_PULSEHA_CONF,
                    config,
                    moduleAuth
                ).subscribe(
                    () => {
                        observer.next();
                        observer.complete();
                    },
                    error => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }

    /**
     * Get the PulseHA global conf via the api
     * @returns {Observable}
     */
    public getConfigGlobal(options?: any): Observable<PulseHAGlobalModel> {
        return new Observable(
            (observer) => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.api.get(
                    SSLEnum.GET_PULSEHA_GLOBAL_CONF,
                    moduleAuth
                ).subscribe({
                    next: (response: any): void => {
                        observer.next(response);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
            }
        );
    }

    /**
     * Update PulseHA global conf via the api
     * @returns {Observable}
     */
    public updateConfigGlobal(config: PulseHAGlobalModel, options?: any) {
        return new Observable(
            (observer) => {
                // Get our selected module details
                const moduleAuth = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.api.put(
                    SSLEnum.PUT_PULSEHA_GLOBAL_CONF,
                    config,
                    moduleAuth
                ).subscribe(
                    () => {
                        observer.next();
                        observer.complete();
                    },
                    error => {
                        observer.error(error);
                        observer.complete();
                    }
                );
            }
        );
    }
}
